/**
 * useOrdersSync Hook
 *
 * Global WebSocket subscription for order status updates.
 * Subscribes to user.{userGuid}.orders — receives ORDER_STATUS_CHANGED events
 * when order fulfillment/payment status changes. Invalidates order history cache.
 */

import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import {
  useWebSocketOptional,
  useRealtimeEventOptional,
} from '@teletienda/ws-client/react';
import { useAuth } from '@teletienda/shared-auth-ui';

const ORDER_STATUS_CHANGED_EVENT = 'ORDER_STATUS_CHANGED' as any;
const userOrdersTopic = (userGuid: string) => `user.${userGuid}.orders`;

export function useOrdersSync(): void {
  const queryClient = useQueryClient();
  const { user, isAuthenticated } = useAuth();
  const { isConnected, subscribe, unsubscribe } = useWebSocketOptional();

  // Subscribe to user's orders topic
  useEffect(() => {
    if (!isConnected || !isAuthenticated || !user?.userGuid) return;

    const topic = userOrdersTopic(user.userGuid);
    subscribe([topic]);

    return () => {
      unsubscribe([topic]);
    };
  }, [isConnected, isAuthenticated, user?.userGuid, subscribe, unsubscribe]);

  // Handle ORDER_STATUS_CHANGED events
  useRealtimeEventOptional(
    ORDER_STATUS_CHANGED_EVENT,
    useCallback(
      () => {
        // Invalidate order list + all timelines
        queryClient.invalidateQueries({ queryKey: ['userOrders'] });
        queryClient.invalidateQueries({ queryKey: ['orderTimeline'] });
      },
      [queryClient],
    ),
    [user?.userGuid],
  );
}
